import {chakra} from '@chakra-ui/react'
import {CatchBoundary} from '@tanstack/react-router'
import {flexRender, Row} from '@tanstack/react-table'
import {memo} from 'react'
import {BaseRowData} from '../../../types/index.js'

interface InfiniteTableRowV2Props<RowData extends BaseRowData> {
  row: Row<RowData> | undefined
  selected: boolean
  highlighted?: boolean
  /** Fixed pixel column widths + sticky leading columns (paired with InfiniteDataTableV2 scrollX). */
  scrollX?: boolean
  frozenLeftById?: Map<string, number> | null
  /** Number of leading sticky columns; the last one gets a right border to separate it from the scroll area. */
  frozenColumns?: number
}

export const InfiniteTableRowV2 = memo(
  function InfiniteTableRowV2Component<RowData extends BaseRowData>({
    row,
    highlighted,
    scrollX,
    frozenLeftById,
    frozenColumns = 0,
  }: InfiniteTableRowV2Props<RowData>) {
    return (
      <chakra.div
        display="flex"
        alignItems="center"
        height="100%"
        width="100%"
        position="relative"
        borderBottomWidth="1px"
        borderColor="border"
        data-e2e={row?.id}
      >
        {highlighted && (
          <chakra.div position="absolute" left="0" top="0" bottom="0" width="2px" bg="cyan.solid" />
        )}
        <CatchBoundary getResetKey={() => 'reset'} onCatch={console.error}>
          {row?.getVisibleCells().map((cell, index) => {
            const meta = cell.column.columnDef.meta as
              | Record<string, (row: unknown) => Record<string, unknown> | undefined>
              | undefined
            const props = meta?.getCellContainerProps?.(cell.row.original)
            const size = cell.column.getSize()
            const frozenLeft = scrollX ? frozenLeftById?.get(cell.column.id) : undefined
            const sticky = frozenLeft !== undefined
            const isLastFrozen = scrollX && index === frozenColumns - 1

            return (
              <chakra.div
                key={cell.id}
                position={sticky ? 'sticky' : 'relative'}
                left={sticky ? `${frozenLeft}px` : undefined}
                zIndex={sticky ? 2 : undefined}
                bg={sticky ? 'bg' : undefined}
                _groupHover={sticky ? {bg: 'bg.muted'} : undefined}
                alignSelf={sticky ? 'stretch' : undefined}
                borderRightWidth={isLastFrozen ? '1px' : undefined}
                borderColor={isLastFrozen ? 'border' : undefined}
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                display="flex"
                alignItems="center"
                fontSize="sm"
                padding="3"
                {...(scrollX
                  ? {width: `${size}px`, minWidth: `${size}px`, flex: '0 0 auto'}
                  : {
                      flex: '1 1 0%',
                      width: '100%',
                      maxWidth: `${size}%`,
                      minWidth: cell.column.columnDef.minSize ? `${cell.column.columnDef.minSize}px` : undefined,
                    })}
                data-e2e={cell.id}
                {...props}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </chakra.div>
            )
          })}
        </CatchBoundary>
      </chakra.div>
    )
  },
  (prev, next) => {
    if (prev.row?.original !== next.row?.original) return false
    if (prev.selected !== next.selected) return false
    if (prev.highlighted !== next.highlighted) return false
    if (prev.scrollX !== next.scrollX) return false
    if (prev.frozenLeftById !== next.frozenLeftById) return false
    if (prev.frozenColumns !== next.frozenColumns) return false
    return true
  }
)
